import { useRef } from "react";
import { Timestamp } from "@bufbuild/protobuf/wkt";
import { areTimestampsEqual } from "@/utils/proto-utils";
import { fastDeepCopy } from "@/utils/object-utils";
import { isEqual } from "lodash";

export type EqualityCheck<T> = (a: T, b: T) => boolean;
export type OnChangeRef<T> = (oldT: T, newT: T) => void;

export const shallowEquals = <T>(objA: Record<string, T>, objB: Record<string, T>): boolean => {
  if (objA === objB) {
    return true;
  }

  if (Object.keys(objA).length !== Object.keys(objB).length) {
    return false;
  }

  return Object.entries(objA).every(([key, objAVal]) => {
    return objB[key] === objAVal;
  });
};

export const useStableEntity = <T>(
  entity: T,
  comparator: EqualityCheck<T>,
  onChange?: OnChangeRef<T>,
): T => {
  const stableRef = useRef<T>(entity);

  if (!comparator(entity, stableRef.current)) {
    onChange?.(stableRef.current, entity);
    stableRef.current = entity;
  }

  return stableRef.current;
};

const areSetsEqual = <T>(setA: Set<T>, setB: Set<T>): boolean => {
  return setA.size === setB.size && Array.from(setA.keys()).every((elementA) => setB.has(elementA));
};

export const useStableSet = <T>(
  obj: Set<T>,
  comparator: EqualityCheck<Set<T>> = areSetsEqual,
): Set<T> => {
  return useStableEntity(obj, comparator);
};

export const useStableObject = <T>(
  obj: Record<string, T>,
  comparator: EqualityCheck<Record<string, T>> = shallowEquals,
): Record<string, T> => {
  return useStableEntity(obj, comparator);
};

export const useStableTimestamp = (timestamp: Timestamp | null | undefined) =>
  useStableEntity<Timestamp | null | undefined>(timestamp, areTimestampsEqual);

export const useUnstableEntity = <T extends object>(
  entity: T,
  comparator: EqualityCheck<T> = isEqual,
): T => {
  const unstableRef = useRef<T>(fastDeepCopy(entity));

  if (!comparator(entity, unstableRef.current)) {
    unstableRef.current = fastDeepCopy(entity);
  }

  return unstableRef.current;
};
